import { useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';
import { Copy } from 'lucide-react';
import { A, DESTINATIONS } from '@/constants/journey';
import { useLive } from '@/lib/live';
import { copyContract } from '@/components/journey/S00Signal';

export const S02Timetable = () => {
  const { config, reducedMotion } = useLive();
  const sectionRef = useRef(null);
  const boardRef = useRef(null);

  useLayoutEffect(() => {
    if (reducedMotion) return undefined;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        boardRef.current,
        { y: 28, opacity: 0.35 },
        {
          y: 0,
          opacity: 1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
            end: 'top 20%',
            scrub: 0.9,
          },
        }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, [reducedMotion]);

  const configuredContract = config?.placeholders?.CONTRACT_ADDRESS || '';
  const contract = configuredContract && !configuredContract.startsWith('{{') ? configuredContract : 'OFFICIAL CA UNAVAILABLE';

  return (
    <section ref={sectionRef} className="relative h-[165vh]" data-testid="chapter-s02" data-audio-scene="timetable" aria-label="The timetable is all tickers">
      <div className="sticky top-0 flex h-screen items-center justify-center overflow-hidden grain">
        <div className="absolute inset-0" style={{ backgroundImage: `url(${A.tex.brick})`, backgroundSize: '520px', opacity: 0.22 }} />
        <div className="absolute inset-0 bg-[#0b0d10]/82" />

        <div className="relative z-10 w-full max-w-5xl px-4 sm:px-7">
          <p className="mb-2 text-center font-mono text-[10px] tracking-[0.32em] text-[#c9a24a]">SOLANA LINE</p>
          <h2 className="text-center font-display text-4xl tracking-wide text-[#f3e7cf] sm:text-6xl">THE TIMETABLE IS ALL TICKERS.</h2>

          <div ref={boardRef} className="timetable-board mt-7 overflow-hidden" data-testid="departure-board">
            <div className="flex items-center justify-between border-b border-[#f3e7cf]/15 px-4 py-3 sm:px-5">
              <span className="font-mono text-[10px] tracking-[0.22em] text-[#c9a24a]">10 STOPS</span>
              <span className="font-mono text-[10px] tracking-[0.2em] text-[#f3e7cf]/48">DEPOT → LAST BRIDGE</span>
            </div>
            <div className="grid sm:grid-flow-col sm:grid-rows-5">
              {DESTINATIONS.map((stop, index) => (
                <div key={stop.id} className={`grid grid-cols-[3.4rem_5.8rem_1fr] items-center gap-2 border-b border-[#f3e7cf]/10 px-4 py-2.5 sm:px-5 ${index >= 5 ? 'sm:border-l' : ''}`}>
                  <span className="font-display text-xl tabular-nums leading-none text-[#c9a24a]">{index + 1}<small className="ml-1 font-mono text-[8px] tracking-normal text-[#c9a24a]/75">/ 10</small></span>
                  <span className="font-mono text-xs tracking-[0.05em] text-[#f3e7cf]">{stop.ticker}</span>
                  <span className="truncate text-right font-sans text-[11px] font-medium text-[#f3e7cf]/72 sm:text-xs">{stop.place}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 flex flex-col items-stretch gap-3 border-t border-[#f3e7cf]/15 pt-3 sm:flex-row sm:items-center" data-testid="contract-board">
            <div className="min-w-0 flex-1">
              <div className="font-mono text-[9px] tracking-[0.25em] text-[#c9a24a]">CONTRACT ADDRESS</div>
              <div className="select-all truncate font-mono text-xs text-[#f3e7cf]/75" data-testid="contract-address-text">{contract}</div>
            </div>
            <button type="button" onClick={() => copyContract(config)} className="btn-mech flex h-10 shrink-0 items-center justify-center gap-2 border border-[#f3e7cf]/20 px-4 font-mono text-[10px] tracking-[0.14em] text-[#f3e7cf]" data-testid="copy-contract-board-button">
              <Copy size={13} /> COPY CA
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
